/**
 * LiveKit client service for web.
 * Replaces Daily.co for video/audio conferencing.
 */
import {
  Room,
  RoomOptions,
  RoomConnectOptions,
  Track,
  Participant as LiveKitParticipant,
} from 'livekit-client';
import type { ParticipantData } from '../../types/meeting';
import type {
  LiveKitCallbacks,
  JoinRoomOptions,
  LiveKitConnectionState,
} from './livekitClientTypes';
import { bindRoomEventHandlers } from './livekitClientEvents';
import { mapLocalParticipant, mapRemoteParticipant } from './livekitClientParticipants';
import { applyDeviceSelection, createLocalTracks } from './livekitClientDevices';
import {
  LIVEKIT_PUBLISH_DEFAULTS,
  SCREEN_SHARE_CAPTURE_OPTIONS,
  WEB_CAMERA_CAPTURE_OPTIONS,
} from './mediaQuality';

const LIVEKIT_URL = import.meta.env.VITE_LIVEKIT_URL || 'ws://35.222.168.155:7880';
const TOKEN_ENDPOINT = import.meta.env.VITE_LIVEKIT_TOKEN_URL || '/api/getLiveKitToken';

let room: Room | null = null;
const callbacks: LiveKitCallbacks = {};

export interface ScreenShareSnapshot {
  ownerId: string | null;
  track: MediaStreamTrack | null;
}

export interface RoomCameraSnapshot {
  ownerId: string | null;
  track: MediaStreamTrack | null;
}

/**
 * Initializes the LiveKit room object.
 */
export function initializeLiveKit(): Room {
  if (room) {
    return room;
  }

  const roomOptions: RoomOptions = {
    adaptiveStream: false,
    dynacast: true,
    videoCaptureDefaults: WEB_CAMERA_CAPTURE_OPTIONS,
    publishDefaults: LIVEKIT_PUBLISH_DEFAULTS,
    audioCaptureDefaults: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      channelCount: 1,
      sampleRate: 48_000,
      latency: 0.02,
    },
  };

  room = new Room(roomOptions);
  bindRoomEventHandlers(room, callbacks);
  return room;
}

/**
 * Gets the current room object.
 */
export function getRoom(): Room | null {
  return room;
}

/**
 * Fetches a LiveKit token from the server.
 */
async function fetchToken(
  roomName: string,
  participantName: string,
  participantId: string
): Promise<string> {
  const params = new URLSearchParams({
    room: roomName,
    name: participantName,
    id: participantId,
  });

  const response = await fetch(`${TOKEN_ENDPOINT}?${params}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch token: ${response.statusText}`);
  }

  const data = await response.json();
  return data.token;
}

/**
 * Joins a LiveKit room.
 */
export async function joinRoom(options: JoinRoomOptions): Promise<void> {
  if (!room) {
    initializeLiveKit();
  }

  if (room?.state === 'connected') {
    await applyDeviceSelection(room, {
      microphoneDeviceId: options.microphoneDeviceId,
      cameraDeviceId: options.cameraDeviceId,
      speakerDeviceId: options.speakerDeviceId,
    });
    return;
  }

  const token = await fetchToken(
    options.roomName,
    options.displayName,
    options.participantId
  );

  const { audioTrack, videoTrack } = await createLocalTracks(
    options.audioEnabled,
    options.videoEnabled,
    options.microphoneDeviceId,
    options.cameraDeviceId
  );

  let effectiveVideoEnabled = options.videoEnabled;
  let effectiveVideoTrack = videoTrack;
  if (effectiveVideoTrack) {
    const isReady = await waitForVideoTrackReady(effectiveVideoTrack.mediaStreamTrack);
    if (!isReady) {
      effectiveVideoTrack.stop();
      effectiveVideoTrack = undefined;
      effectiveVideoEnabled = false;
    }
  }

  const tracks = [audioTrack, effectiveVideoTrack].filter(Boolean) as NonNullable<typeof audioTrack>[];

  const connectOptions: RoomConnectOptions = {
    autoSubscribe: true,
    // Longer timeouts for mobile networks
    peerConnectionTimeout: 45_000,
    websocketTimeout: 30_000,
    rtcConfig: {
      // Allow both UDP and TCP for better mobile compatibility
      iceTransportPolicy: 'all',
    },
  };

  await room!.connect(LIVEKIT_URL, token, connectOptions);

  applyParticipantMetadata(room!, {
    ...options,
    videoEnabled: effectiveVideoEnabled,
  });

  for (const track of tracks) {
    await room!.localParticipant.publishTrack(track);
  }

  // setSinkId not supported on mobile browsers
  const canSwitchAudioOutput =
    typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;
  if (options.speakerDeviceId && canSwitchAudioOutput) {
    await room!.switchActiveDevice('audiooutput', options.speakerDeviceId);
  }
}

async function waitForVideoTrackReady(
  track: MediaStreamTrack | null | undefined,
  timeoutMs = 800
): Promise<boolean> {
  if (!track) {
    return false;
  }

  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    if (isMediaTrackLive(track) && hasVideoDimensions(track)) {
      return true;
    }

    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  return isMediaTrackLive(track) && hasVideoDimensions(track);
}

function isMediaTrackLive(track: MediaStreamTrack): boolean {
  if (track.readyState !== 'live') {
    return false;
  }

  if (!track.enabled || track.muted) {
    return false;
  }

  return true;
}

function hasVideoDimensions(track: MediaStreamTrack): boolean {
  if (typeof track.getSettings !== 'function') {
    return true;
  }

  const settings = track.getSettings();
  const width = settings.width ?? 0;
  const height = settings.height ?? 0;
  return width > 0 && height > 0;
}

/**
 * Applies metadata to the local participant.
 */
function applyParticipantMetadata(roomInstance: Room, options: JoinRoomOptions): void {
  const metadata = buildParticipantMetadata(options);
  if (!metadata) {
    return;
  }

  roomInstance.localParticipant.setMetadata(JSON.stringify(metadata));
}

/**
 * Builds metadata payload for the local participant.
 */
function buildParticipantMetadata(options: JoinRoomOptions): Record<string, unknown> | null {
  const metadata: Record<string, unknown> = {
    isVR: options.isVR ?? false,
    isHost: options.isHost ?? false,
    cameraOn: options.videoEnabled ?? false,
  };

  if (options.avatarUrl) {
    metadata.avatarUrl = options.avatarUrl;
  }

  if (options.displayName) {
    metadata.displayName = options.displayName;
  }

  return metadata;
}

/**
 * Leaves the current room.
 */
export async function leaveRoom(): Promise<void> {
  if (room) {
    await room.disconnect();
  }
}

/**
 * Destroys the room object.
 */
export function destroyLiveKit(): void {
  if (room) {
    room.disconnect();
    room = null;
  }
}

/**
 * Toggles local audio.
 */
export async function setAudioEnabled(enabled: boolean): Promise<void> {
  if (!room?.localParticipant) {
    return;
  }

  await room.localParticipant.setMicrophoneEnabled(enabled);
  updateParticipantMetadata({ microphoneOn: enabled });
}

/**
 * Toggles local video.
 */
export async function setVideoEnabled(enabled: boolean): Promise<void> {
  if (!room?.localParticipant) {
    return;
  }

  await room.localParticipant.setCameraEnabled(enabled);
  updateParticipantMetadata({ cameraOn: enabled });
}

/**
 * Updates profile metadata for the local participant.
 */
export function updateLocalParticipantProfile(profile: {
  displayName?: string;
  avatarUrl?: string;
}): void {
  if (!room?.localParticipant) {
    return;
  }

  const updates: Record<string, unknown> = {};
  if (profile.displayName) {
    updates.displayName = profile.displayName;
  }

  if (profile.avatarUrl) {
    updates.avatarUrl = profile.avatarUrl;
  }

  if (Object.keys(updates).length === 0) {
    return;
  }

  updateParticipantMetadata(updates);
}

/**
 * Starts screen sharing.
 */
export async function startScreenShare(): Promise<void> {
  if (!room?.localParticipant) {
    return;
  }

  await room.localParticipant.setScreenShareEnabled(true, SCREEN_SHARE_CAPTURE_OPTIONS);
}

/**
 * Stops screen sharing.
 */
export async function stopScreenShare(): Promise<void> {
  if (!room?.localParticipant) {
    return;
  }

  await room.localParticipant.setScreenShareEnabled(false);
}

/**
 * Gets all participants in the room.
 */
export function getParticipants(): ParticipantData[] {
  if (!room) {
    return [];
  }

  const participants: ParticipantData[] = [];

  if (room.localParticipant) {
    participants.push(mapLocalParticipant(room.localParticipant));
  }

  for (const participant of room.remoteParticipants.values()) {
    participants.push(mapRemoteParticipant(participant));
  }

  return participants;
}

/**
 * Gets the local participant.
 */
export function getLocalParticipant(): ParticipantData | null {
  if (!room?.localParticipant) {
    return null;
  }

  return mapLocalParticipant(room.localParticipant);
}

/**
 * Gets a remote participant by ID.
 */
export function getRemoteParticipant(participantId: string): ParticipantData | null {
  if (!room) {
    return null;
  }

  const participant = room.remoteParticipants.get(participantId);
  if (!participant) {
    return null;
  }

  return mapRemoteParticipant(participant);
}

/**
 * Resolves the active screen-share owner and video track from current publications.
 */
export function getCurrentScreenShareState(): ScreenShareSnapshot {
  if (!room) {
    return { ownerId: null, track: null };
  }

  let ownerId: string | null = null;
  let track: MediaStreamTrack | null = null;

  for (const participant of getAllParticipantsInRoom()) {
    for (const publication of participant.trackPublications.values()) {
      if (publication.source !== Track.Source.ScreenShare) {
        continue;
      }

      if (!isPublicationPotentiallyActive(publication)) {
        continue;
      }

      ownerId = ownerId ?? participant.identity;
      const mediaTrack = publication.track?.mediaStreamTrack ?? null;
      if (isUsableVideoTrack(mediaTrack)) {
        return { ownerId: participant.identity, track: mediaTrack };
      }
    }
  }

  return { ownerId, track };
}

/**
 * Resolves the active room-camera owner and video track from current publications.
 */
export function getCurrentRoomCameraState(): RoomCameraSnapshot {
  if (!room) {
    return { ownerId: null, track: null };
  }

  let ownerId: string | null = null;
  let track: MediaStreamTrack | null = null;

  for (const participant of getAllParticipantsInRoom()) {
    for (const publication of participant.trackPublications.values()) {
      if (publication.source !== Track.Source.Camera) {
        continue;
      }

      if (!isRoomCameraTrackName(publication.trackName)) {
        continue;
      }

      if (!isPublicationPotentiallyActive(publication)) {
        continue;
      }

      ownerId = ownerId ?? participant.identity;
      const mediaTrack = publication.track?.mediaStreamTrack ?? null;
      if (isUsableVideoTrack(mediaTrack)) {
        return { ownerId: participant.identity, track: mediaTrack };
      }
    }
  }

  return { ownerId, track };
}

/**
 * Returns current LiveKit room connection state.
 */
export function getConnectionState(): LiveKitConnectionState {
  if (!room) {
    return 'disconnected';
  }

  switch (room.state) {
    case 'connected':
      return 'connected';
    case 'connecting':
      return 'connecting';
    case 'reconnecting':
      return 'reconnecting';
    default:
      return 'disconnected';
  }
}

function updateParticipantMetadata(update: Record<string, unknown>): void {
  if (!room?.localParticipant) {
    return;
  }

  const current = parseParticipantMetadata(room.localParticipant.metadata);
  const next = { ...(current || {}), ...update };
  room.localParticipant.setMetadata(JSON.stringify(next));
}

function parseParticipantMetadata(metadata?: string | null): Record<string, unknown> | null {
  if (!metadata) {
    return null;
  }

  try {
    return JSON.parse(metadata) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function getAllParticipantsInRoom(): LiveKitParticipant[] {
  if (!room) {
    return [];
  }

  const participants: LiveKitParticipant[] = [];
  if (room.localParticipant) {
    participants.push(room.localParticipant);
  }

  for (const participant of room.remoteParticipants.values()) {
    participants.push(participant);
  }

  return participants;
}

function isPublicationPotentiallyActive(publication: {
  isMuted?: boolean;
  track?: { mediaStreamTrack?: MediaStreamTrack | null } | null;
  isSubscribed?: boolean;
}): boolean {
  if (publication.isMuted) {
    return false;
  }

  if (publication.track?.mediaStreamTrack) {
    return true;
  }

  return publication.isSubscribed === true;
}

function isUsableVideoTrack(track: MediaStreamTrack | null | undefined): track is MediaStreamTrack {
  if (!track || track.kind !== 'video') {
    return false;
  }

  if (track.readyState !== 'live') {
    return false;
  }

  if (!track.enabled || track.muted) {
    return false;
  }

  return true;
}

function isRoomCameraTrackName(trackName?: string): boolean {
  if (!trackName) {
    return false;
  }

  return trackName === 'room_camera' || trackName.includes('room_camera');
}

/**
 * Switches to a specific camera.
 */
export async function switchCamera(deviceId: string): Promise<void> {
  if (!room?.localParticipant) {
    return;
  }

  await room.switchActiveDevice('videoinput', deviceId);
}

/**
 * Switches to a specific microphone.
 */
export async function switchMicrophone(deviceId: string): Promise<void> {
  if (!room?.localParticipant) {
    return;
  }

  await room.switchActiveDevice('audioinput', deviceId);
}

/**
 * Sets event callbacks.
 */
export function setCallbacks(nextCallbacks: LiveKitCallbacks): void {
  callbacks.onParticipantJoined = nextCallbacks.onParticipantJoined ?? null;
  callbacks.onParticipantLeft = nextCallbacks.onParticipantLeft ?? null;
  callbacks.onParticipantUpdated = nextCallbacks.onParticipantUpdated ?? null;
  callbacks.onTrackStarted = nextCallbacks.onTrackStarted ?? null;
  callbacks.onTrackStopped = nextCallbacks.onTrackStopped ?? null;
  callbacks.onConnectionStateChanged = nextCallbacks.onConnectionStateChanged ?? null;
  callbacks.onError = nextCallbacks.onError ?? null;
}

export {
  applyDeviceSelection,
  getAvailableCameras,
  getAvailableMicrophones,
  getAvailableSpeakers,
} from './livekitClientDevices';
